import React, { useState, useRef } from "react";
import { Button, Box, Menu, MenuItem, ListItemText } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";

interface AppModuleTabsProps {
  variant?: "navbar" | "card" | "minimal";
  maxVisibleItems?: number; 
}

const categories = [
  { name: "SALES", path: "/sales" },
  { name: "LMS", path: "/courses" },
  { name: "ADMIN", path: "/user-management" },
  { name: "OPS", path: "/stream-management" },
  { name: "LEGACY", path: "/chema" },
];

export const AppModuleTabs: React.FC<AppModuleTabsProps> = ({
  variant = "navbar",
  maxVisibleItems = 4, 
}) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };


  const visibleCategories = categories.slice(0, maxVisibleItems);
  const overflowCategories = categories.slice(maxVisibleItems);

  const isMoreActive = overflowCategories.some((category) =>
    location.pathname.startsWith(category.path)
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: variant === "card" ? 2 : 3,
        flexWrap: "wrap",
        ...(variant === "card" && {
          justifyContent: "center",
          px: 2,
        }),
      }}
    >
      {/* Render visible categories */}
      {visibleCategories.map((category) => {
        const isActive = location.pathname.startsWith(category.path);
        return (
          <Button
            key={category.name}
            component={Link}
            to={category.path}
            sx={{
              color: isActive ? "primary.main" : "text.primary",
              fontWeight: isActive ? "bold" : "medium",
              fontSize: variant === "card" ? "0.9rem" : "0.95rem",
              textTransform: "none",
              minWidth: "auto",
              padding: variant === "card" ? "8px 16px" : "6px 12px",
              borderRadius: "6px",
              backgroundColor: isActive ? "action.selected" : "transparent",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "primary.main",
                //textDecoration: "underline",
              },
              ...(variant === "minimal" && {
                padding: "4px 8px",
                fontSize: "0.875rem",
              }),
            }}
          >
            {category.name}
          </Button>
        );
      })}

      {/* Render "More" dropdown if there are overflow categories */}
      {overflowCategories.length > 0 && (
        <>
          <Button
            ref={moreButtonRef}
            onClick={handleOpenMenu}
            endIcon={<ExpandMore />}
            sx={{
              color: isMoreActive ? "primary.main" : "text.primary",
              fontWeight: isMoreActive ? "bold" : "medium",
              fontSize: variant === "card" ? "0.9rem" : "0.95rem",
              textTransform: "none",
              minWidth: "auto",
              padding: variant === "card" ? "8px 16px" : "6px 12px",
              borderRadius: "6px",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "action.hover",
              },
              ...(variant === "minimal" && {
                padding: "4px 8px",
                fontSize: "0.875rem",
              }),
            }}
          >
            More
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 160,
              },
            }}
          >
            {overflowCategories.map((category) => {
              const isActive = location.pathname.startsWith(category.path);

              return (
                <MenuItem
                  key={category.name}
                  component={Link}
                  to={category.path}
                  onClick={handleCloseMenu}
                  sx={{
                    color: isActive ? "primary.main" : "text.primary",
                    fontWeight: isActive ? "bold" : "normal",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                    }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};
